import {
  Smartphone,
  Shield,
  WifiOff,
  RefreshCw,
  ScanLine,
  FolderOpen,
  Apple,
  Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import "./index.css";

const features = [
  {
    icon: ScanLine,
    title: "Instant Access",
    description: "Your barcode ready in seconds. No fumbling at checkout.",
  },
  {
    icon: Shield,
    title: "Privacy First",
    description: "Your data stays yours. We never sell to third parties.",
  },
  {
    icon: WifiOff,
    title: "Works Offline",
    description: "Access all your cards without an internet connection.",
  },
  {
    icon: RefreshCw,
    title: "Cross-Device Sync",
    description: "Seamlessly sync across all your devices.",
  },
  {
    icon: Smartphone,
    title: "Easy Import",
    description: "Scan barcodes or add manually in seconds.",
  },
  {
    icon: FolderOpen,
    title: "Stay Organized",
    description: "Categories, search, and favorites for quick access.",
  },
];

function Logo() {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-coral to-coral-dark flex items-center justify-center overflow-hidden">
        <svg viewBox="0 0 28 20" fill="none" className="w-6 h-5">
          {/* Card background */}
          <rect width="28" height="20" rx="3" fill="#0F1419" fillOpacity="0.9"/>
          {/* Barcode stripe area */}
          <rect y="4" width="28" height="4" fill="#1A2028"/>
          {/* Barcode lines */}
          <rect x="2" y="4.5" width="1.5" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          <rect x="4.5" y="4.5" width="1" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          <rect x="6" y="4.5" width="2" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          <rect x="9" y="4.5" width="1" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          <rect x="11" y="4.5" width="1.5" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          <rect x="13.5" y="4.5" width="1" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          <rect x="15.5" y="4.5" width="2" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          <rect x="18.5" y="4.5" width="1" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          <rect x="20" y="4.5" width="1.5" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          <rect x="22.5" y="4.5" width="1" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          <rect x="24.5" y="4.5" width="1.5" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
          {/* Card details */}
          <rect x="4" y="12" width="12" height="2" rx="1" fill="#FF6B4A" fillOpacity="0.6"/>
          <rect x="4" y="16" width="8" height="2" rx="1" fill="#FF6B4A" fillOpacity="0.4"/>
        </svg>
      </div>
      <span className="text-xl font-semibold tracking-tight">CardHoldr</span>
    </div>
  );
}

function PhoneMockup() {
  return (
    <div className="phone-mockup animate-fade-in delay-300">
      <div className="phone-screen">
        <div className="loyalty-card loyalty-card-1">
          <div className="barcode">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="barcode-line" />
            ))}
          </div>
        </div>
        <div className="loyalty-card loyalty-card-2">
          <div className="barcode">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="barcode-line" />
            ))}
          </div>
        </div>
        <div className="loyalty-card loyalty-card-3">
          <div className="barcode">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="barcode-line" />
            ))}
          </div>
        </div>
        <div className="loyalty-card loyalty-card-4">
          <div className="barcode">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="barcode-line" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StoreButtons({ className = "" }: { className?: string }) {
  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
      <Button
        variant="secondary"
        size="lg"
        disabled
        className="h-14 px-6 gap-3 bg-surface-light/50 border border-border hover:bg-surface-light cursor-not-allowed opacity-60 grayscale-[30%]"
      >
        <Apple className="w-6 h-6" />
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-none">
            Soon on
          </div>
          <div className="text-sm font-semibold leading-tight">App Store</div>
        </div>
      </Button>
      <Button
        variant="secondary"
        size="lg"
        disabled
        className="h-14 px-6 gap-3 bg-surface-light/50 border border-border hover:bg-surface-light cursor-not-allowed opacity-60 grayscale-[30%]"
      >
        <Play className="w-6 h-6" />
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-none">
            Soon on
          </div>
          <div className="text-sm font-semibold leading-tight">Google Play</div>
        </div>
      </Button>
    </div>
  );
}

function Hero() {
  return (
    <section className="min-h-screen flex items-center justify-center px-6 py-20">
      <div className="max-w-6xl w-full mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text content */}
          <div className="text-center lg:text-left">
            <div className="animate-fade-in">
              <Logo />
            </div>
            
            <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] animate-fade-in delay-100">
              Your Wallet,{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-coral to-coral-light">
                Simplified
              </span>
            </h1>
            
            <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-lg mx-auto lg:mx-0 animate-fade-in delay-200">
              All your loyalty cards in one beautiful app. The privacy-focused 
              alternative you've been waiting for.
            </p>
            
            <div className="mt-8 animate-fade-in delay-300">
              <StoreButtons />
            </div>
          </div>
          
          {/* Phone mockup */}
          <div className="flex justify-center lg:justify-end">
            <PhoneMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight animate-fade-in-up">
            Everything you need,{" "}
            <span className="text-coral">nothing you don't</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto animate-fade-in-up delay-100">
            Built for speed, privacy, and simplicity. No bloat, no tracking, no nonsense.
          </p>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className={`glass-card glass-card-hover rounded-2xl p-6 animate-fade-in-up`}
              style={{ animationDelay: `${(index + 2) * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-coral/20 to-coral/5 flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-coral" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="py-24 px-6">
      <div className="max-w-3xl mx-auto text-center">
        <div className="glass-card rounded-3xl p-8 sm:p-12 animate-fade-in-up">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-coral to-coral-dark flex items-center justify-center mx-auto mb-6 animate-pulse-glow overflow-hidden">
            <svg viewBox="0 0 28 20" fill="none" className="w-10 h-8">
              <rect width="28" height="20" rx="3" fill="#0F1419" fillOpacity="0.9"/>
              <rect y="4" width="28" height="4" fill="#1A2028"/>
              <rect x="2" y="4.5" width="1.5" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="4.5" y="4.5" width="1" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="6" y="4.5" width="2" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="9" y="4.5" width="1" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="11" y="4.5" width="1.5" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="13.5" y="4.5" width="1" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="15.5" y="4.5" width="2" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="18.5" y="4.5" width="1" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="20" y="4.5" width="1.5" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="22.5" y="4.5" width="1" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="24.5" y="4.5" width="1.5" height="3" fill="#F5F5F5" fillOpacity="0.85"/>
              <rect x="4" y="12" width="12" height="2" rx="1" fill="#FF6B4A" fillOpacity="0.6"/>
              <rect x="4" y="16" width="8" height="2" rx="1" fill="#FF6B4A" fillOpacity="0.4"/>
            </svg>
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Coming Soon to Your Pocket
          </h2>
          
          <p className="mt-4 text-muted-foreground max-w-md mx-auto">
            We're putting the finishing touches on CardHoldr. 
            The wait will be worth it.
          </p>
          
          <div className="mt-8 flex justify-center">
            <StoreButtons />
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-8 px-6 border-t border-border/50">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <Logo />
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <a 
            href="mailto:hello@novaspark.dev" 
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            hello@novaspark.dev
          </a>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear() === 2025 ? "2025" : `2025–${new Date().getFullYear()}`} CardHoldr. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export function App() {
  return (
    <>
      <div className="gradient-bg" />
      <main>
        <Hero />
        <Features />
        <CTA />
        <Footer />
      </main>
    </>
  );
}

export default App;
